import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemberListApi } from '@hero/api-hooks';
import type { Member } from '@hero/schema';
import type { RedirectProp } from '@hero/web-hooks';
import { UserIcon } from '@hero/web-icons';
import { MemberTable } from './MemberTable';
import { ButtonCreateEntity } from '../../../app/ButtonCreateEntity';
import { PageBack } from '../../../app/PageBack';
import { TableEmptyState } from '../../../app/Table';
import { MemberDrawer } from '../Drawer';
import { MemberEditModal } from '../Modal';

export interface MemberTableManagerProps {
  /** Rare: override the default API function. Not needed for carrier vs customer — the hook resolves the correct SDK from context. */
  apiFn?: (req: any) => Promise<any>;
  displayForUser?: 'customer' | 'carrier';
  /**
   * Render a `<PageBack>` above the table. Hidden by default; pass
   * `true` to default to the More screen, or a typed route name /
   * config for a custom destination. See `RedirectProp` for the full
   * shape.
   */
  redirect?: RedirectProp;
}

export function MemberTableManager({
  apiFn,
  displayForUser = 'customer',
  redirect,
}: MemberTableManagerProps) {
  const { t } = useTranslation(['m-member']);

  const {
    data: members,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useMemberListApi({}, apiFn ? { apiFn } : undefined);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ type: next });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  const filterTypes = useMemo(
    () =>
      displayForUser === 'carrier'
        ? (['admin', 'dispatcher', 'driver'] as const)
        : (['admin', 'dispatcher', 'employee'] as const),
    [displayForUser],
  );

  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const type of filterTypes) {
      counts[type] = members.filter((m: Member) => m.type === type).length;
    }
    return { total: members.length, counts };
  }, [members, filterTypes]);

  const statusFilterOptions = useMemo(
    () =>
      filterTypes.map(type => ({
        value: type,
        label: t(`m-member:types.${type}.title`),
        className:
          type === 'admin' ? 'bg-primary-100 text-logoBlue' : 'bg-success-100 text-success',
      })),
    [t, filterTypes],
  );

  const statusFilterCounts = useMemo(
    () => filterTypes.map(type => ({ value: type, count: stats.counts[type] ?? 0 })),
    [stats, filterTypes],
  );

  const emptyState = (
    <TableEmptyState
      icon={<UserIcon className="h-7 w-7" />}
      title={t('m-member:table.empty.title')}
      description={t('m-member:table.empty.description')}
      action={
        <ButtonCreateEntity
          label={t('m-member:table.empty.cta')}
          onClick={() => setIsCreateOpen(true)}
        />
      }
    />
  );

  return (
    <>
      {redirect ? <PageBack redirect={redirect} /> : null}
      <MemberTable
        members={members}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        onSort={handleSort}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        emptyState={emptyState}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}>
        <MemberTable.Head>
          <MemberTable.Search />
          <div className="flex items-center gap-3">
            <MemberTable.Badge
              options={statusFilterOptions}
              counts={statusFilterCounts}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              showCounts={false}
            />
            <ButtonCreateEntity
              label={t('m-member:modal.create_member_title')}
              onClick={() => setIsCreateOpen(true)}
            />
          </div>
        </MemberTable.Head>
      </MemberTable>

      <MemberDrawer syncUrl />
      <MemberEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        displayForUser={displayForUser}
      />
    </>
  );
}
